
import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import { Mail, Trash2, Filter, Search } from 'lucide-react';

const EmailLogs: React.FC = () => {
    const { emailLogs, deleteEmailLog } = useData();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<'all' | 'confirmation' | 'inquiry' | 'quote' | 'general'>('all');

    const filteredLogs = emailLogs.filter(log => {
        const matchesSearch = (log.recipient?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (log.subject?.toLowerCase() || '').includes(searchTerm.toLowerCase());
        const matchesType = filterType === 'all' || log.type === filterType;
        return matchesSearch && matchesType;
    });

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'confirmation': return 'bg-green-100 text-green-800';
            case 'inquiry': return 'bg-blue-100 text-blue-800';
            case 'quote': return 'bg-purple-100 text-purple-800';
            case 'general': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Sent': return 'bg-blue-100 text-blue-800';
            case 'Delivered': return 'bg-green-100 text-green-800';
            case 'Opened': return 'bg-purple-100 text-purple-800';
            case 'Failed': return 'bg-red-100 text-red-800';
            case 'Bounced': return 'bg-orange-100 text-orange-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Email Logs</h1>
                    <p className="text-gray-500">View all sent emails and their delivery status.</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-4">
                <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search by recipient or subject..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-automax-red focus:border-transparent"
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Filter size={20} className="text-gray-400" />
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value as any)}
                            className="px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-automax-red focus:border-transparent"
                        >
                            <option value="all">All Types</option>
                            <option value="confirmation">Confirmation</option>
                            <option value="inquiry">Inquiry</option>
                            <option value="quote">Quote</option>
                            <option value="general">General</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Email Logs Table */}
            <div className="bg-white rounded-sm shadow-sm border border-gray-100 overflow-hidden">
                {filteredLogs.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">
                        <Mail size={48} className="mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium">No email logs found.</p>
                        <p className="text-sm mt-2">Emails will appear here once they are sent.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Sent At</th>
                                    <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Recipient</th>
                                    <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Subject</th>
                                    <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Type</th>
                                    <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredLogs.map(log => (
                                    <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="p-4 whitespace-nowrap text-sm text-gray-600">
                                            <div>{new Date(log.sentAt).toLocaleDateString()}</div>
                                            <div className="text-xs text-gray-400">{new Date(log.sentAt).toLocaleTimeString()}</div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center text-sm text-gray-900">
                                                <Mail size={14} className="mr-2 text-gray-400" />
                                                {log.recipient}
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <p className="text-sm text-gray-900 font-medium">{log.subject}</p>
                                            {log.body && (
                                                <p className="text-xs text-gray-500 mt-1 truncate max-w-md">{log.body.substring(0, 80)}...</p>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span className={`px-2 py-1 rounded-sm text-xs font-bold uppercase ${getTypeColor(log.type)}`}>
                                                {log.type}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <span className={`px-2 py-1 rounded-sm text-xs font-bold uppercase ${getStatusColor(log.status)}`}>
                                                {log.status}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right">
                                            <button
                                                onClick={() => {
                                                    if (window.confirm(`Are you sure you want to delete this email log to ${log.recipient}?`)) {
                                                        deleteEmailLog(log.id);
                                                    }
                                                }}
                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                                title="Delete Email Log"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Summary Stats */}
            <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-bold mb-4">Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{emailLogs.length}</div>
                        <div className="text-xs text-gray-500 uppercase">Total</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{emailLogs.filter(l => l.type === 'confirmation').length}</div>
                        <div className="text-xs text-gray-500 uppercase">Confirmation</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{emailLogs.filter(l => l.type === 'inquiry').length}</div>
                        <div className="text-xs text-gray-500 uppercase">Inquiry</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">{emailLogs.filter(l => l.type === 'quote').length}</div>
                        <div className="text-xs text-gray-500 uppercase">Quote</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">{emailLogs.filter(l => l.status === 'Failed').length}</div>
                        <div className="text-xs text-gray-500 uppercase">Failed</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailLogs;
